// src/components/mitchRoom/modelOutputs/utils.js
import { RETURN_PREDICTION_THRESHOLD, BLANK_PREDICTION_THRESHOLD } from '../../../utils/constants';
import { getLogo as getLogoUtil } from '../../../utils/imageUtils';

/**
 * Get team logo by name (re-exported from shared utils)
 */
export const getLogo = getLogoUtil;

/**
 * Determine if a fixture is home or away for a given team
 */
export const getHomeAway = (fixture, teamOptaId, teamMap) => {
  if (!fixture) return null;
  const team = teamMap.get(teamOptaId);
  if (!team) return null;

  if (fixture.team_h === team.fplID) return 'Home';
  if (fixture.team_a === team.fplID) return 'Away';
  return null;
};

/**
 * Get opponent team for a given team in a fixture
 */
export const getOpponent = (fixture, teamOptaId, teamMap, allTeams) => {
  if (!fixture) return null;
  const team = teamMap.get(teamOptaId);
  if (!team) return null;

  const oppFplId = fixture.team_h === team.fplID ? fixture.team_a : fixture.team_h;

  // Find opponent by fplID
  const opponent = allTeams.find((t) => t.team_fplID === oppFplId);
  return opponent;
};

/**
 * Determine match result: 'Win', 'Draw', 'Loss'
 */
export const getMatchResult = (clubScore, oppScore) => {
  if (clubScore > oppScore) return 'Win';
  if (clubScore === oppScore) return 'Draw';
  return 'Loss';
};

/**
 * Get result badge variant for a match result.
 * Returns semantic variant keys matching `components/ui/badge`:
 * 'success' (Win) | 'destructive' (Loss) | 'outline' (Draw / unknown).
 */
export const getResultColor = (result) => {
  switch (result) {
    case 'Win':
      return 'success';
    case 'Loss':
      return 'destructive';
    case 'Draw':
    default:
      return 'outline';
  }
};

/**
 * Get position label from element_type
 */
export const getPositionLabel = (elementType) => {
  const positions = {
    1: 'GKP',
    2: 'DEF',
    3: 'MID',
    4: 'FWD',
  };
  return positions[elementType] || 'UNK';
};

/**
 * Apply filters to team results
 */
export const filterTeamResults = (results, filters, teamMap, fixtureMap) => {
  const { teamFilter, seasonFilter, homeAwayFilter } = filters;

  return results.filter((result) => {
    // Team filter
    if (teamFilter.length > 0 && !teamFilter.includes(result.club_optaID)) {
      return false;
    }

    // Season filter
    if (seasonFilter.length > 0 && !seasonFilter.includes(result.compSeasonID)) {
      return false;
    }

    // Home/Away filter
    if (homeAwayFilter !== 'All') {
      const fixture = fixtureMap.get(result.fixture_optaID);
      if (fixture) {
        const homeAway = getHomeAway(fixture, result.club_optaID, teamMap);
        if (homeAway !== homeAwayFilter) {
          return false;
        }
      }
    }

    return true;
  });
};

/**
 * Apply filters to player results
 * Now uses home_away directly from result data instead of fixtureMap lookup
 */
export const filterPlayerResults = (results, filters, elementMap, teamMap, fixtureMap) => {
  const { teamFilter, positionFilter, seasonFilter, homeAwayFilter } = filters;

  return results.filter((result) => {
    const player = elementMap.get(result.player_optaID);
    if (!player) return false;

    // Team filter
    if (teamFilter.length > 0 && !teamFilter.includes(result.club_optaID)) {
      return false;
    }

    // Position filter - use player data or result.element_type
    const position = player?.singular_name_short;
    if (positionFilter.length > 0 && position && !positionFilter.includes(position)) {
      return false;
    }

    // Season filter
    if (seasonFilter.length > 0 && !seasonFilter.includes(result.compSeasonID)) {
      return false;
    }

    // Home/Away filter - use home_away directly from result data
    if (homeAwayFilter !== 'All') {
      // Normalize home_away from data ('h'/'a' or 'Home'/'Away')
      const homeAway = result.home_away
        ? result.home_away.toLowerCase() === 'h' || result.home_away.toLowerCase() === 'home'
          ? 'Home'
          : 'Away'
        : null;
      if (homeAway !== homeAwayFilter) {
        return false;
      }
    }

    return true;
  });
};

/**
 * Check prediction accuracy for returns
 * Returns: 'correct', 'false_positive', 'false_negative', or null
 */
export const checkReturnPrediction = (expectedPoints, actualPoints) => {
  const predictedReturn = expectedPoints >= RETURN_PREDICTION_THRESHOLD;
  const actualReturn = actualPoints >= RETURN_PREDICTION_THRESHOLD;

  if (predictedReturn && actualReturn) return 'correct'; // Predicted return and got return
  if (predictedReturn && !actualReturn) return 'false_positive'; // Predicted return but blanked
  if (!predictedReturn && actualReturn) return 'false_negative'; // Didn't predict return but got one
  return null; // Not applicable
};

/**
 * Check prediction accuracy for blanks
 * Returns: 'correct', 'false_positive', 'false_negative', or null
 * Blank = < 4 points (strictly less than threshold)
 */
export const checkBlankPrediction = (expectedPoints, actualPoints) => {
  const predictedBlank = expectedPoints < BLANK_PREDICTION_THRESHOLD;
  const actualBlank = actualPoints < BLANK_PREDICTION_THRESHOLD;

  if (predictedBlank && actualBlank) return 'correct'; // Predicted blank and got blank
  if (predictedBlank && !actualBlank) return 'false_positive'; // Predicted blank but returned
  if (!predictedBlank && actualBlank) return 'false_negative'; // Didn't predict blank but blanked
  return null; // Not applicable
};

/**
 * Format percentage for display
 */
export const formatPercent = (value) => {
  if (value == null) return '-';
  return `${value.toFixed(1)}%`;
};

/**
 * Format score display
 */
export const formatScore = (homeScore, awayScore) => {
  if (homeScore == null || awayScore == null) return '-';
  return `${homeScore} - ${awayScore}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Availability Prediction Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Check if starting prediction was correct
 * @param {number} probStarting - Predicted probability of starting (0-1)
 * @param {number} actualStarted - 1 if started, 0 if not
 * @returns {'correct'|'incorrect'|null}
 */
export const checkStartingPrediction = (probStarting, actualStarted) => {
  if (probStarting == null || actualStarted == null) return null;
  const predictedStart = probStarting >= 0.5;
  const didStart = actualStarted === 1;
  return predictedStart === didStart ? 'correct' : 'incorrect';
};

/**
 * Check if playing prediction was correct
 * @param {number} probPlaying - Predicted probability of playing (0-1)
 * @param {number} actualPlayed - 1 if played, 0 if not
 * @returns {'correct'|'incorrect'|null}
 */
export const checkPlayingPrediction = (probPlaying, actualPlayed) => {
  if (probPlaying == null || actualPlayed == null) return null;
  const predictedPlay = probPlaying >= 0.5;
  const didPlay = actualPlayed === 1;
  return predictedPlay === didPlay ? 'correct' : 'incorrect';
};

/**
 * Calculate minutes prediction error
 * @param {number} expectedMinutes - Predicted minutes
 * @param {number} actualMinutes - Actual minutes played
 * @returns {number|null} Absolute error
 */
export const getMinutesError = (expectedMinutes, actualMinutes) => {
  if (expectedMinutes == null || actualMinutes == null) return null;
  return Math.abs(expectedMinutes - actualMinutes);
};

/**
 * Build a map of availability data keyed by player_optaID-fixture_optaID
 * @param {Array} availabilityData - Array of availability records
 * @returns {Map} Map keyed by "player_optaID-fixture_optaID"
 */
export const buildAvailabilityMap = (availabilityData) => {
  const map = new Map();
  if (!availabilityData || !Array.isArray(availabilityData)) return map;

  availabilityData.forEach((record) => {
    const key = `${record.player_optaID}-${record.fixture_optaID}`;
    map.set(key, record);
  });

  return map;
};
